import React, { createContext, useCallback, useContext, useState } from 'react';

import { MysteryBoxQuestion } from './mysteryBoxConfig';

type SizeSelections = Record<string, string>;
type StyleSelections = Record<string, string[]>;

interface MysteryBoxSelectionsContextValue {
  sizeSelections: SizeSelections;
  selectSize: (questionId: string, value: string) => void;
  styleSelections: StyleSelections;
  toggleStyle: (
    questionId: string,
    label: string,
    isSurpriseMe: boolean
  ) => void;
  isSizeComplete: boolean;
  hasSoldOutSizeSelected: boolean;
  sizeQuestions: MysteryBoxQuestion[];
  setSizeQuestions: (questions: MysteryBoxQuestion[]) => void;
  styleQuestions: MysteryBoxQuestion[];
  setStyleQuestions: (questions: MysteryBoxQuestion[]) => void;
}

const MysteryBoxSelectionsContext =
  createContext<MysteryBoxSelectionsContextValue | null>(null);

export function MysteryBoxSelectionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sizeSelections, setSizeSelections] = useState<SizeSelections>({});
  const [styleSelections, setStyleSelections] = useState<StyleSelections>({});
  const [sizeQuestions, setSizeQuestions] = useState<MysteryBoxQuestion[]>([]);
  const [styleQuestions, setStyleQuestions] = useState<MysteryBoxQuestion[]>(
    []
  );

  const isSizeComplete =
    sizeQuestions.length > 0 &&
    sizeQuestions.every(
      (q) => sizeSelections[String(q.mysteryBoxPreferenceTypeId)] != null
    );

  const hasSoldOutSizeSelected = sizeQuestions.some((q) => {
    const selectedLabel = sizeSelections[String(q.mysteryBoxPreferenceTypeId)];
    if (!selectedLabel) {
      return false;
    }
    const answer = q.answers.find((a) => a.label === selectedLabel);
    return answer?.disabled === true;
  });

  const selectSize = useCallback((questionId: string, value: string) => {
    setSizeSelections((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const toggleStyle = useCallback(
    (questionId: string, label: string, isSurpriseMe: boolean) => {
      setStyleSelections((prev) => {
        const current = prev[questionId] ?? [];
        if (isSurpriseMe) {
          return { ...prev, [questionId]: [] };
        }
        const isSelected = current.includes(label);
        const next = isSelected
          ? current.filter((l) => l !== label)
          : [...current, label];
        return { ...prev, [questionId]: next };
      });
    },
    []
  );

  return React.createElement(
    MysteryBoxSelectionsContext.Provider,
    {
      value: {
        sizeSelections,
        selectSize,
        styleSelections,
        toggleStyle,
        isSizeComplete,
        hasSoldOutSizeSelected,
        sizeQuestions,
        setSizeQuestions,
        styleQuestions,
        setStyleQuestions,
      },
    },
    children
  );
}

export function useMysteryBoxSelectionsContext(): MysteryBoxSelectionsContextValue {
  const ctx = useContext(MysteryBoxSelectionsContext);
  if (ctx == null) {
    throw new Error(
      'useMysteryBoxSelectionsContext must be used within MysteryBoxSelectionsProvider'
    );
  }
  return ctx;
}

export function isSurpriseMeActive(
  styleSelections: StyleSelections,
  questionId: string
): boolean {
  const selections = styleSelections[questionId];
  return !selections || selections.length === 0;
}

export function useIsMysteryBoxSizeComplete(
  questions: MysteryBoxQuestion[]
): boolean {
  const { sizeSelections } = useMysteryBoxSelectionsContext();
  return (
    questions.length > 0 &&
    questions.every(
      (q) => sizeSelections[String(q.mysteryBoxPreferenceTypeId)] != null
    )
  );
}
